import { useCallback, ReactElement, ReactNode, cloneElement, useState, ComponentProps } from 'react';
import classnames from 'classnames';

import { StaticDataFetcherItem } from '@hh.ru/magritte-common-data-provider';
import { keyboardKeys, keyboardMatch } from '@hh.ru/magritte-common-keyboard';
import { Action } from '@hh.ru/magritte-ui-action';
import { ActionBar } from '@hh.ru/magritte-ui-action-bar';
import { BottomSheet, BottomSheetFooter } from '@hh.ru/magritte-ui-bottom-sheet';
import { Button } from '@hh.ru/magritte-ui-button';
import { ButtonElement } from '@hh.ru/magritte-ui-button-stack';
import { Cell, CellText } from '@hh.ru/magritte-ui-cell';
import { ArrowPathClockwiseSize24, CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { SearchInput } from '@hh.ru/magritte-ui-input';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { NavigationBar } from '@hh.ru/magritte-ui-navigation-bar';
import { SelectError } from '@hh.ru/magritte-ui-select/SelectError';
import { SelectOption } from '@hh.ru/magritte-ui-select/SelectOption';
import { getChecked } from '@hh.ru/magritte-ui-select/getChecked';
import { isArraysEquals } from '@hh.ru/magritte-ui-select/isArraysEquals';
import {
    OnChangeAction,
    SelectButtons,
    SelectBottomSheetProps,
    RenderOptionTypeProps,
    RenderHeaderProps,
} from '@hh.ru/magritte-ui-select/types';

import styles from './select-bottom-sheet.less';

const SelectOptionDefault = <DataType extends StaticDataFetcherItem>({
    data,
    input,
    type,
}: RenderOptionTypeProps<DataType>): ReactElement => {
    if (type === 'delimiter') {
        return (
            <CellText maxLines={1} style="secondary">
                {data.text}
            </CellText>
        );
    }
    if (type === 'label') {
        return <CellText maxLines={1}>{data.text}</CellText>;
    }
    return (
        <Cell Element="div" align="center" right={input}>
            <CellText maxLines={1}>{data.text}</CellText>
        </Cell>
    );
};

const RenderButtonsContainerDefault = ({ clearButton, applyChangesButton }: SelectButtons): ReactNode => {
    const primaryActions = [clearButton, applyChangesButton].filter(Boolean);
    return <ActionBar type="mobile" primaryActions={primaryActions as ButtonElement[]} />;
};

const RenderHeaderDefault = ({
    onCloseBottomSheet,
    searchValue,
    setSearchValue,
    searchable,
    headerTitle,
    onSearchSubmit,
    optionsCount,
    onCloseBottomSheetWithUpdateValues,
}: RenderHeaderProps): ReactNode => {
    return (
        <NavigationBar
            title={headerTitle}
            right={
                <Action
                    icon={CrossOutlinedSize24}
                    onClick={onCloseBottomSheet}
                    data-qa="select-bottom-sheet-navigation-close"
                />
            }
            options={
                searchable && (
                    <SearchInput
                        clearable
                        size="medium"
                        value={searchValue}
                        onChange={setSearchValue}
                        onKeyDown={(event) => {
                            if (onSearchSubmit && keyboardMatch(event.nativeEvent, keyboardKeys.Enter)) {
                                onSearchSubmit(
                                    event,
                                    onCloseBottomSheet,
                                    optionsCount,
                                    onCloseBottomSheetWithUpdateValues
                                );
                            }
                        }}
                        data-qa="bottom-sheet-navigation-input"
                        autoComplete="off"
                    />
                )
            }
        />
    );
};

export const SelectBottomSheet = <MultipleType extends boolean, DataType extends StaticDataFetcherItem>({
    type,
    name,
    multiple,
    clearButton: _clearButton,
    applyChangesButton: _applyChangesButton,
    useInitialValuesOnClearAction,
    loading,
    error,
    renderHeader = RenderHeaderDefault,
    renderItem = SelectOptionDefault,
    renderButtonsContainer = RenderButtonsContainerDefault,
    renderAlertOnExitWithoutSaving,
    headerTitle,
    onClose,
    visible,
    optionList,
    optionsListRef,
    searchValue,
    setSearchValue,
    searchable,
    value: values,
    onChange,
    clearSelectedValues,
    setInitialValues,
    isLimitExceeded,
    initialValuesOnOpening,
    setSelectedValues,
    onBottomSheetOpen,
    onBottomSheetClose,
    onSearchSubmit,
    onApply,
    onClear,
    emptyOptionsHint,
    emptyOptionsSearchHint,
    searchInProgress,
    internalError,
    reload,
    innerErrorTrls,
    optionDataQa,
    ...props
}: SelectBottomSheetProps<MultipleType, DataType>): ReactElement => {
    const [alertVisible, setAlertVisible] = useState(false);
    const onCloseAlert = () => setAlertVisible(false);
    const showAlertOnExitWithoutSaving = Boolean(renderAlertOnExitWithoutSaving);
    const showErrorPlaceholder = Boolean(error);

    const onCloseWithAlertCheck = () => {
        if (showAlertOnExitWithoutSaving && multiple) {
            const haveUnsavingChanges = !isArraysEquals<string>(
                initialValuesOnOpening.current?.map((item) => item.value) ?? [],
                // eslint-disable-next-line no-nested-ternary
                Array.isArray(values) ? values.map((item) => item.value) : values ? [values.value] : []
            );
            if (haveUnsavingChanges) {
                setAlertVisible(true);
            } else {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const clearButton =
        _clearButton &&
        cloneElement(_clearButton, {
            key: 'clearButton',
            size: 'medium',
            'data-qa': 'magritte-select-clear-action-on-xs',
            onClick: () => {
                if (useInitialValuesOnClearAction) {
                    setInitialValues();
                } else {
                    clearSelectedValues();
                }
                onClose();
                onClear?.();
            },
        } as Partial<ComponentProps<typeof Button>>);

    const needAddChangesButton = multiple && _applyChangesButton;
    const applyChangesButton =
        (needAddChangesButton &&
            cloneElement(_applyChangesButton, {
                key: 'applyChangesButton',
                size: 'medium',
                'data-qa': 'magritte-select-apply-on-xs',
                onClick: () => {
                    setSelectedValues();
                    onClose();
                    onApply?.({ isMobile: true, searchValue });
                },
            } as Partial<ComponentProps<typeof Button>>)) ||
        undefined;

    const handleChange: OnChangeAction<DataType> = useCallback(
        (...args) => {
            if (!multiple) {
                onClose();
            }
            onChange(...args);
        },
        [multiple, onChange, onClose]
    );

    const haveClearOrApplyChangesButtons = clearButton || applyChangesButton;
    const footer = !showErrorPlaceholder ? (
        !loading &&
        haveClearOrApplyChangesButtons &&
        renderButtonsContainer({
            clearButton,
            applyChangesButton,
        })
    ) : (
        <BottomSheetFooter>{error && error.resetButton}</BottomSheetFooter>
    );

    const emptyOptionsHintVisible =
        optionList.length === 0 &&
        !searchValue &&
        !!emptyOptionsHint &&
        !loading &&
        !showErrorPlaceholder &&
        !searchInProgress &&
        !internalError;

    const emptyOptionsSearchHintVisible =
        optionList.length === 0 &&
        searchValue &&
        !!emptyOptionsSearchHint &&
        !loading &&
        !showErrorPlaceholder &&
        !searchInProgress &&
        !internalError;

    const onCloseBottomSheetWithUpdateValues = () => {
        setSelectedValues();
        onCloseWithAlertCheck();
    };

    const optionsVisible =
        !loading &&
        !emptyOptionsSearchHintVisible &&
        !emptyOptionsHintVisible &&
        !internalError &&
        !showErrorPlaceholder;

    return (
        <>
            <BottomSheet
                {...props}
                ref={optionsListRef}
                allowScrollWhileFocused
                keyboardOverlaysContent={false}
                visible={visible}
                showDivider={false}
                withContentPaddings={showErrorPlaceholder}
                header={renderHeader({
                    onCloseBottomSheet: onCloseWithAlertCheck,
                    searchValue,
                    setSearchValue,
                    searchable,
                    headerTitle,
                    onSearchSubmit,
                    optionsCount: optionList.length,
                    onCloseBottomSheetWithUpdateValues,
                })}
                onClose={onCloseWithAlertCheck}
                footer={
                    internalError && innerErrorTrls ? (
                        <BottomSheetFooter>
                            <Button
                                mode="primary"
                                style="accent"
                                size="medium"
                                onClick={reload}
                                stretched
                                icon={<ArrowPathClockwiseSize24 />}
                            >
                                {innerErrorTrls.reloadButton}
                            </Button>
                        </BottomSheetFooter>
                    ) : (
                        footer
                    )
                }
                onAppear={onBottomSheetOpen}
                onAfterExit={onBottomSheetClose}
            >
                {internalError && innerErrorTrls && <SelectError innerErrorTrls={innerErrorTrls} reload={reload} />}
                {emptyOptionsSearchHintVisible && (
                    <div className={styles.fullScreenPlaceholder}>{emptyOptionsSearchHint}</div>
                )}
                {emptyOptionsHintVisible && <div className={styles.fullScreenPlaceholder}>{emptyOptionsHint}</div>}
                {loading && !showErrorPlaceholder && (
                    <div className={styles.fullScreenPlaceholder}>
                        <Loader size={24} />
                    </div>
                )}
                {showErrorPlaceholder && <div className={styles.errorWrapper}>{error && error.placeholder}</div>}
                {optionsVisible && (
                    <div
                        className={classnames(styles.selectBottomSheet, styles[`select-bottom-sheet_${type}`])}
                        data-qa="magritte-select-option-list-on-xs"
                    >
                        {optionList.map(({ disabled: _disabled, type: optionType, ...option }) => {
                            const checked = Boolean(option.data && getChecked<DataType>(option.data, values ?? []));
                            const disabled = !checked && (_disabled || isLimitExceeded);
                            return (
                                <SelectOption
                                    key={option.data.value}
                                    name={name}
                                    checked={checked}
                                    onChange={handleChange}
                                    render={renderItem}
                                    disabled={disabled}
                                    type={optionType || type}
                                    plain={type === 'label'}
                                    multiple={multiple}
                                    data-qa={optionDataQa}
                                    isXS
                                    {...option}
                                />
                            );
                        })}
                    </div>
                )}
            </BottomSheet>
            {renderAlertOnExitWithoutSaving?.({
                alertVisible,
                onCloseAlert,
                onCloseBottomSheetAndAlert: () => {
                    onCloseAlert();
                    onClose();
                },
            })}
        </>
    );
};
